import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

export default function Navbar({ lang }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const prefix = lang === "es" ? "/es" : "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: `${prefix}/services`, label: t("nav.services") },
    { to: `${prefix}/about`, label: t("nav.about") },
    { to: `${prefix}/portfolio`, label: t("nav.portfolio") },
    { to: `${prefix}/testimonials`, label: t("nav.testimonials") },
    { to: `${prefix}/contact`, label: t("nav.contact") },
  ];

  const toggleLang = () => {
    const path = location.pathname;
    if (lang === "es") navigate(path.replace(/^\/es/, "") || "/");
    else navigate(`/es${path === "/" ? "" : path}`);
  };

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled || open ? "bg-white/80 backdrop-blur-xl shadow-[0_1px_0_0_rgba(11,19,32,0.08)]" : "bg-navy/0"
      }`}
    >
      <div className="px-6 md:px-12 max-w-[1400px] mx-auto flex items-center justify-between h-20">
        <Link to={prefix || "/"} data-testid="nav-logo" className="relative flex items-center h-14 md:h-16">
          <img
            src="/assets/logo-light.png"
            alt="VIALFA Handyman Services"
            className={`absolute h-14 md:h-16 w-auto transition-opacity duration-300 ${scrolled || open ? "opacity-0" : "opacity-100"}`}
          />
          <img
            src="/assets/logo-dark.png"
            alt="VIALFA Handyman Services"
            className={`h-14 md:h-16 w-auto transition-opacity duration-300 ${scrolled || open ? "opacity-100" : "opacity-0"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 hover:text-orange ${
                isActive(l.to) ? "text-orange" : scrolled ? "text-navy" : "text-offwhite"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className={`text-xs font-bold tracking-[0.15em] px-2 py-1 border transition-colors duration-200 ${
              scrolled || open ? "border-navy/20 text-navy hover:border-orange hover:text-orange" : "border-offwhite/30 text-offwhite hover:border-orange hover:text-orange"
            }`}
          >
            {lang === "es" ? "ES | EN" : "EN | ES"}
          </button>
          <a
            href="tel:17867746692"
            data-testid="nav-phone"
            className={`hidden md:flex items-center gap-2 text-sm font-bold transition-colors duration-200 hover:text-orange ${scrolled ? "text-navy" : "text-offwhite"}`}
          >
            <Phone size={15} /> 786-774-6692
          </a>
          <Link
            to={`${prefix}/estimate`}
            data-testid="nav-estimate-cta"
            className="hidden sm:inline-flex items-center bg-orange hover:bg-orange-bright text-white text-sm font-bold px-5 py-2.5 transition-[background-color,transform] duration-200 hover:scale-105 active:scale-95"
          >
            {t("nav.estimate")}
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${scrolled || open ? "text-navy" : "text-offwhite"}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-sandline"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className={`text-lg font-heading font-bold ${isActive(l.to) ? "text-orange" : "text-navy"}`}>
                  {l.label}
                </Link>
              ))}
              <a href="tel:17867746692" className="flex items-center gap-2 text-navy font-bold">
                <Phone size={16} /> 786-774-6692
              </a>
              <Link to={`${prefix}/estimate`} className="mt-2 inline-flex justify-center bg-orange text-white font-bold px-5 py-3">
                {t("nav.estimate")}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
